import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import {
  generateCustomAttributeTypes,
  getActionOptions,
  getConditionOptions,
  getCustomAttributeInputType,
  getOperatorTypes,
  isACustomAttribute,
  getFileName,
  getAiEmailAttachments,
  normalizeAiEmailAttachmentBlobIds,
  getDefaultConditions,
  getDefaultActions,
  filterCustomAttributes,
  generateAutomationPayload,
  getStandardAttributeInputType,
  isCustomAttribute,
  generateCustomAttributes,
  matchOptionsByIds,
  resolveMultiSelectConditionValues,
  CALL_MULTI_SELECT_CONDITION_KEYS,
} from 'dashboard/helper/automationHelper';
import { REENGAGEMENT_COUNT_CONDITION, AUTOMATION_RULE_EVENTS, AUTOMATION_ACTION_TYPES, KIVO_NO_ENGAGEMENT_WAIT_MINUTES, normalizeKivoNoEngagementWaitMinutes } from 'dashboard/routes/dashboard/settings/automation/constants';
import {
  defaultAiEmailSchedule,
  normalizeAiEmailSchedule,
} from 'dashboard/helper/aiEmailSchedule';
import { mapGetters } from 'vuex';
import boards from '../../api/boards';
import macros from '../../api/macros';

export default {
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      campaigns: 'campaigns/getAllCampaigns',
      contacts: 'contacts/getContacts',
      inboxes: 'inboxes/getInboxes',
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      boards: 'boards/getBoards',
      macros: 'macros/getMacros',
      whatsInboxes: 'inboxes/getWhatsAppTemplates',
      currentAccountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    automationRuleEvents() {
      return this.filterAutomationFeatureItems(AUTOMATION_RULE_EVENTS);
    },
    automationActionTypes() {
      return this.filterAutomationFeatureItems(AUTOMATION_ACTION_TYPES);
    },
    booleanFilterOptions() {
      return [
        {
          id: true,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
        },
        {
          id: false,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
        },
      ];
    },

    statusFilterOptions() {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      const options = Object.keys(statusFilters).map(status => {
        return {
          id: status,
          name: statusFilters[status].TEXT,
        };
      });

      // Avoid duplicate "All" when translations already include it
      if (!options.some(option => option.id === 'all')) {
        options.push({
          id: 'all',
          name: this.$t('CHAT_LIST.FILTER_ALL'),
        });
      }

      return options;
    },
  },
  methods: {
    getFileName,
    getAiEmailAttachments,
    filterAutomationFeatureItems(items = []) {
      return items.filter(item => {
        if (!item.featureFlag) return true;
        return this.isFeatureEnabledonAccount(
          this.currentAccountId,
          item.featureFlag
        );
      });
    },
    onEventChange() {
      this.automation.conditions = this.hydrateAutomationConditions(
        getDefaultConditions(this.automation.event_name)
      );
      this.automation.actions = getDefaultActions(this.automation.event_name);
    },
    hydrateAutomationConditions(conditions) {
      return conditions.map(condition => {
        if (
          !CALL_MULTI_SELECT_CONDITION_KEYS.includes(condition.attribute_key)
        ) {
          return condition;
        }

        return {
          ...condition,
          values: resolveMultiSelectConditionValues(
            condition.attribute_key,
            condition.values
          ),
        };
      });
    },
    getAttributes(key) {
      return this.filterAutomationFeatureItems(
        this.automationTypes[key].conditions
      );
    },
    getInputType(key) {
      const customAttribute = isACustomAttribute(this.allCustomAttributes, key);
      if (customAttribute) {
        return getCustomAttributeInputType(
          customAttribute.attribute_display_type
        );
      }
      const type = this.getAutomationType(key);
      return type.inputType;
    },
    getOperators(key) {
      if (this.mode === 'edit') {
        const customAttribute = isACustomAttribute(
          this.allCustomAttributes,
          key
        );
        if (customAttribute) {
          return getOperatorTypes(customAttribute.attribute_display_type);
        }
      }
      const type = this.getAutomationType(key);
      return type.filterOperators;
    },
    getAutomationType(key) {
      const flag = this.automationTypes[this.automation.event_name].conditions.find(
        condition => condition.key === key
      );
      return this.automationTypes[this.automation.event_name].conditions.find(
        condition => condition.key === key
      );
    },
    getCustomAttributeType(key) {
      const fromEvent = this.automationTypes[this.automation.event_name]?.conditions?.find(
        item => item.key === key
      )?.customAttributeType;
      if (fromEvent) return fromEvent;

      const defs = this.allCustomAttributes || [];
      const match = defs.find(attr => attr.attribute_key === key);
      return match ? String(match.attribute_model) : '';
    },
    getConditionDropdownValues(type) {
      const {
        agents,
        allCustomAttributes: customAttributes,
        booleanFilterOptions,
        campaigns,
        contacts,
        inboxes,
        labels,
        statusFilterOptions,
        teams,
        boards,
      } = this;
      
      return getConditionOptions({
        agents,
        booleanFilterOptions,
        campaigns,
        contacts,
        customAttributes,
        inboxes,
        labels,
        statusFilterOptions,
        teams,
        languages,
        countries,
        type,
        boards,
      });
    },
    appendNewCondition() {
      this.automation.conditions.push(
        ...this.hydrateAutomationConditions(
          getDefaultConditions(this.automation.event_name)
        )
      );
      const list = this.automation.conditions;
      for (let i = 0; i < list.length - 1; i += 1) {
        const row = list[i];
        if (row && (row.query_operator == null || row.query_operator === '')) {
          this.$set(row, 'query_operator', 'and');
        }
      }
    },
    appendNewAction() {
      this.automation.actions.push(...getDefaultActions());
    },
    removeFilter(index) {
      if (this.automation.conditions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.CONDITION.DELETE_MESSAGE'));
      } else {
        this.automation.conditions.splice(index, 1);
      }
    },
    removeAction(index) {
      if (this.automation.actions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.ACTION.DELETE_MESSAGE'));
      } else {
        this.automation.actions.splice(index, 1);
      }
    },
    submitAutomation() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      const automation = generateAutomationPayload(this.automation);
      this.$emit('saveAutomation', automation, this.mode);
    },
    resetFilter(index, currentCondition) {
      if (currentCondition.attribute_key === 'meeting_present') {
        this.automation.conditions[index].filter_operator = 'is_present';
        this.automation.conditions[index].values = 'yes';
        return;
      }

      this.automation.conditions[index].filter_operator = this.automationTypes[
        this.automation.event_name
      ].conditions.find(
        condition => condition.key === currentCondition.attribute_key
      ).filterOperators[0].value;

      const inputType = this.getInputType(currentCondition.attribute_key);
      this.automation.conditions[index].values =
        inputType === 'multi_select' ? [] : '';
    },
    showUserInput(type, attributeKey) {
      if (attributeKey === 'meeting_present') return false;
      return !(type === 'is_present' || type === 'is_not_present');
    },
    hideOperatorForCondition(attributeKey) {
      return attributeKey === 'meeting_present';
    },
    showActionInput(action) {
      if (
        action === 'send_email_to_team' ||
        action === 'send_message' ||
        action === 'send_kivo_notification' ||
        action === 'send_kivo_notification_on_no_engagement'
      )
        return false;
      const type = this.automationActionTypes.find(i => i.key === action)
        .inputType;
      return !!type;
    },
    resetAction(index) {
      const actionName = this.automation.actions[index].action_name;
      if (actionName === 'send_kivo_notification') {
        this.automation.actions[index].action_params = { url: '', text: '' };
        return;
      }
      if (actionName === 'send_kivo_notification_on_no_engagement') {
        this.automation.actions[index].action_params = {
          url: '',
          text: '',
          wait_time_minutes: KIVO_NO_ENGAGEMENT_WAIT_MINUTES.DEFAULT,
        };
        return;
      }
      if (actionName === 'send_ai_generated_email') {
        this.automation.actions[index].action_params = {
          inbox_id: null,
          macro_id: null,
          attachment_blob_ids: [],
          schedule: defaultAiEmailSchedule(),
        };
        return;
      }
      if (actionName === 'send_whatsapp_template') {
        this.automation.actions[index].action_params = {
          inbox_id: null,
          macro_id: null,
        };
        this.automation.actions[index].delay_minutes = '';
        return;
      }
      if (actionName === 'add_macro') {
        this.automation.actions[index].action_params = [];
        this.automation.actions[index].delay_minutes = '';
        return;
      }
      this.automation.actions[index].action_params = [];
    },
    manifestConditions(automation) {
      const customAttributes = filterCustomAttributes(this.allCustomAttributes);
      const conditions = automation.conditions.map(condition => {
        const customAttr = isCustomAttribute(
          customAttributes,
          condition.attribute_key
        );
        let inputType = 'plain_text';
        if (customAttr) {
          inputType = getCustomAttributeInputType(customAttr.type);
        } else {
          inputType = getStandardAttributeInputType(
            this.automationTypes,
            automation.event_name,
            condition.attribute_key
          );
        }
        if (condition.attribute_key === 'meeting_present') {
          const isNo = condition.filter_operator === 'is_not_present';
          return {
            ...condition,
            query_operator: condition.query_operator || 'and',
            values: isNo ? 'no' : 'yes',
          };
        }
        if (
          condition.attribute_key === 'lead_quality_score' ||
          condition.attribute_key === 'reengagement_count'
        ) {
          const raw = Array.isArray(condition.values)
            ? condition.values[0]
            : condition.values;
          return {
            ...condition,
            query_operator: condition.query_operator || 'and',
            values: raw ?? '',
          };
        }
        if (inputType === 'plain_text' || inputType === 'date') {
          return {
            ...condition,
            query_operator: condition.query_operator || 'and',
            values: condition.values[0],
          };
        }
        return {
          ...condition,
          query_operator: condition.query_operator || 'and',
          values: matchOptionsByIds(
            this.getConditionDropdownValues(condition.attribute_key),
            condition.values
          ),
        };
      });
      return conditions;
    },
    generateActionsArray(action) {
      const params = action.action_params;
      let actionParams = [];
      const inputType = this.automationActionTypes.find(
        item => item.key === action.action_name
      ).inputType;
      if (inputType === 'multi_select' || inputType === 'search_select') {
        const options = this.getActionDropdownValues(action.action_name);

        if (action.action_name === 'add_macro') {
          const first = Array.isArray(params) ? params[0] : params;
          const macroIds =
            first && typeof first === 'object' && first.macro_ids
              ? first.macro_ids
              : params;
          actionParams = matchOptionsByIds(options, macroIds);
        } else if (action.action_name === 'add_board') {
          const raw = Array.isArray(params) ? params[0] : params;
          let boardId;
          if (raw !== null && raw !== undefined && typeof raw === 'object') {
            boardId = raw.board_id ?? raw.id;
          } else {
            boardId = raw;
          }
          actionParams = options.filter(
            opt => boardId != null && String(opt.id) === String(boardId)
          );
        } else {
          actionParams = matchOptionsByIds(options, params);
        }
      } else if (inputType === 'team_message') {
        const source = Array.isArray(params) ? params[0] : params;
        const options = this.getActionDropdownValues(action.action_name) || [];
        actionParams = {
          team_ids: matchOptionsByIds(options, source?.team_ids),
          message: source?.message ?? '',
        };
      } else if (inputType === 'ai_generated_email') {
        const source = Array.isArray(params) ? params[0] : params;
        actionParams = {
          inbox_id: source?.inbox_id ?? null,
          macro_id: source?.macro_id ?? null,
          attachment_blob_ids: normalizeAiEmailAttachmentBlobIds(source),
          schedule: normalizeAiEmailSchedule(source?.schedule),
        };
      } else if (inputType === 'send_whatsapp_template') {
        const source = Array.isArray(params) ? params[0] : params;
        actionParams = {
          inbox_id: source?.inbox_id ?? null,
          macro_id: source?.macro_id ?? null,
        };
      } else if (inputType === 'kivo_webhook') {
        const source = Array.isArray(params) ? params[0] : params;
        actionParams = {
          url: source?.url ?? '',
          text: source?.text ?? '',
        };
      } else if (inputType === 'kivo_no_engagement_webhook') {
        const source = Array.isArray(params) ? params[0] : params;
        actionParams = {
          url: source?.url ?? '',
          text: source?.text ?? '',
          wait_time_minutes: normalizeKivoNoEngagementWaitMinutes(
            source?.wait_time_minutes
          ),
        };
      } else actionParams = [...params];
      return actionParams;
    },
    manifestActions(automation) {
      return automation.actions.map(action => {
        const p = action.action_params;
        const hasParams = Array.isArray(p)
          ? p.length > 0
          : p !== null &&
            p !== undefined &&
            p !== '' &&
            (typeof p !== 'object' || Object.keys(p).length > 0);

        let actionParams = [];
        if (hasParams) {
          actionParams = this.generateActionsArray(action);
        }

        const next = { ...action, action_params: actionParams };

        if (action.action_name === 'add_board' && actionParams.length) {
          const raw = Array.isArray(action.action_params)
            ? action.action_params[0]
            : action.action_params;
          const sid = raw?.status_id;
          if (sid != null) {
            const board = actionParams[0];
            const statuses =
              board?.statuses_attributes || board?.statuses || [];
            const st = statuses.find(s => String(s.id) === String(sid));
            if (st) next.status = st;
          }
        }

        if (
          action.action_name === 'add_macro' ||
          action.action_name === 'send_whatsapp_template'
        ) {
          const raw = Array.isArray(action.action_params)
            ? action.action_params[0]
            : action.action_params;
          next.delay_minutes =
            raw && typeof raw === 'object' && raw.delay_minutes != null
              ? raw.delay_minutes
              : '';
        }

        return next;
      });
    },
    formatAutomation(automation) {
      this.automation = {
        ...automation,
        conditions: this.manifestConditions(automation),
        actions: this.manifestActions(automation),
      };
    },
    getActionDropdownValues(type) {
      const { agents, labels, teams, boards, macros} = this;
      return getActionOptions({ agents, labels, teams, languages, type , boards, macros});
    },
    manifestReengagementCountCondition() {
      Object.keys(this.automationTypes).forEach(eventKey => {
        const conditions = this.automationTypes[eventKey]?.conditions;
        if (!conditions) return;

        const exists = conditions.some(c => c.key === 'reengagement_count');
        if (!exists) {
          conditions.push({ ...REENGAGEMENT_COUNT_CONDITION });
        }
      });
    },
    manifestCustomAttributes() {
      const conversationCustomAttributesRaw = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('conversation_attribute');

      const contactCustomAttributesRaw = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('contact_attribute');
      const conversationCustomAttributeTypes = generateCustomAttributeTypes(
        conversationCustomAttributesRaw,
        'conversation_attribute'
      );
      const contactCustomAttributeTypes = generateCustomAttributeTypes(
        contactCustomAttributesRaw,
        'contact_attribute'
      );
      let manifestedCustomAttributes = generateCustomAttributes(
        conversationCustomAttributeTypes,
        contactCustomAttributeTypes,
        this.$t('AUTOMATION.CONDITION.CONVERSATION_CUSTOM_ATTR_LABEL'),
        this.$t('AUTOMATION.CONDITION.CONTACT_CUSTOM_ATTR_LABEL')
      );
      this.automationTypes.message_created.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_created.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_updated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_opened.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.board_status_change.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.lead_quality_score_generated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.contact_joined_meeting.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.call_ended.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.whatsapp_flow_submitted.conditions.push(
        ...manifestedCustomAttributes
      );
      //  this.automationTypes.contact_created.conditions.push(
      //   ...manifestedCustomAttributes
      // );
    },
  },
};
